import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Cog6ToothIcon, VideoCameraIcon, ChevronDownIcon, FilmIcon, PhotoIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ProjectAssetsDrawer from '../components/projectAssets/ProjectAssetsDrawer';
import { VideoEditorProvider, useVideoEditor } from '../components/SuperVideoEditor/context/VideoEditorContext';
import Timeline from '../components/SuperVideoEditor/Timeline';
import PreviewAreaWithTabs from '../components/SuperVideoEditor/PreviewAreaWithTabs';
import VideoEditorSync from '../components/SuperVideoEditor/VideoEditorSync';
import ResourcePanel from '../components/SuperVideoEditor/ResourcePanel';
import GenerationProgress from '../components/SuperVideoEditor/GenerationProgress';
import BatchVideoGenerationModal from '../components/episodes/BatchVideoGenerationModal';
import ExportLoadingOverlay from '../components/SuperVideoEditor/ExportLoadingOverlay';
import GlobalLoading from '../components/GlobalLoading';
import EditableHeaderTitle from '../components/common/EditableHeaderTitle';
import { useEpisode } from '../hooks/useEpisode';
import { useEpisodeVideoClips } from '../hooks/useEpisodeVideoClips';
import { useBatchVideoGeneration } from '../hooks/useBatchVideoGeneration';
import { useVideoEdit } from '../hooks/useVideoEdit';
import { useFFmpegExport } from '../hooks/useFFmpegExport';
import { useStoryboardExport } from '../hooks/useStoryboardExport';
import { useResourceWebSocket } from '../hooks/useResourceWebSocket';
import { IVideoClip, IVideoClipResource } from '@packages/global';
import { useToast } from '../stores/toastStore';
import { episodeApi } from '../services/user';

// 清理组件：在卸载时停止播放和所有音频
function PlaybackCleanup() {
  const { actions } = useVideoEditor();
  const pauseRef = useRef(actions.pause);

  useEffect(() => {
    pauseRef.current = actions.pause;
  }, [actions.pause]);

  useEffect(() => {
    return () => {
      pauseRef.current();
      // 停止所有音频元素
      document.querySelectorAll('audio').forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  return null;
}

// 同步暂停函数到父组件
function PauseActionSync({ pauseActionRef }: { pauseActionRef: React.MutableRefObject<(() => void) | null> }) {
  const { actions } = useVideoEditor();

  useEffect(() => {
    pauseActionRef.current = () => {
      actions.pause();
      // 停止所有音频元素
      document.querySelectorAll('audio').forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, [actions.pause, pauseActionRef]);

  return null;
}

export default function ProjectDetailPage() {
  const { episodeId: id } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['editor', 'common']);
  const [currentEpisodeId, setCurrentEpisodeId] = useState<string>(id || '');
  const { success, error } = useToast();
  const pauseActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (id) {
      setCurrentEpisodeId(id);
    }
  }, [id]);

  const [selectedVideoClip, setSelectedVideoClip] = useState<IVideoClip | null>(null);
  const [activeResourceTab, setActiveResourceTab] = useState<'image' | 'audio' | 'video' | 'storyboard'>('image');
  const [imageFrameType, setImageFrameType] = useState<'start' | 'end' | 'storyboard'>('start');
  const lastViewRef = useRef<'video' | 'audio' | 'text' | null>(null); // 追踪上一次的视图
  const [showAssetsDrawer, setShowAssetsDrawer] = useState(false);
  const [showBatchVideoModal, setShowBatchVideoModal] = useState(false);
  const [showBatchVideoDropdown, setShowBatchVideoDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isResourcePanelCollapsed, setIsResourcePanelCollapsed] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const [isTitleSaving, setIsTitleSaving] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<{ videoUrl: string, filename: string } | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [showPreExportLoading, setShowPreExportLoading] = useState(false);
  const [isBatchGeneratingPrompts, setIsBatchGeneratingPrompts] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const batchVideoDropdownRef = useRef<HTMLDivElement>(null);
  const { episode, loading: episodesLoading, refreshEpisode } = useEpisode(currentEpisodeId);
  const { videoClips, refreshVideoClip, loading: videoClipsLoading, refetch: refetchVideoClips } = useEpisodeVideoClips(currentEpisodeId);
  const { batchGenerateVideos, loading: batchVideoLoading, exportVideo, pollExportStatus, exportProgress: cloudExportProgress } = useBatchVideoGeneration();
  const { editConfig, updateEditConfig, saveEditConfig, loading: editConfigLoading, initializeEditConfig, refreshAllClipsDuration } = useVideoEdit(episode, videoClips);
  const { isExporting: isLocalExporting, exportProgress: localExportProgress, startExport, cancelExport } = useFFmpegExport();
  const {
    isExporting: isStoryboardExporting,
    exportProgress: storyboardExportProgress,
    startExport: startStoryboardExport,
    cancelExport: cancelStoryboardExport
  } = useStoryboardExport();

  // 统一资源轮询
  const handleResourceCompleted = useCallback(async (completedClipIds: string[]) => {
    console.log('资源完成回调:', completedClipIds);
    // 刷新完成的 clips 数据
    for (const clipId of completedClipIds) {
      await refreshVideoClip(clipId);
    }
    // 只在有新资源完成时才刷新 editConfig
    // 注意：refreshAllClipsDuration 内部已有防抖保护（2秒间隔）
    if (refreshAllClipsDuration && completedClipIds.length > 0) {
      await refreshAllClipsDuration();
    }
  }, [refreshVideoClip, refreshAllClipsDuration]);

  const {
    resourceStatusMap,
    pendingClipIds,
    isPolling: isResourcePolling,
    triggerPoll: triggerResourcePoll
  } = useResourceWebSocket(videoClips, {
    pollingInterval: 3000,
    enabled: isPageReady,
    onResourceCompleted: handleResourceCompleted
  });

  // 更新单个 clip 的资源状态（用于 form 提交后立即更新）
  const handleResourceStatusUpdate = useCallback((clipId: string, resources: IVideoClipResource[]) => {
    // 触发一次轮询来更新状态
    triggerResourcePoll();
  }, [triggerResourcePoll]);

  // 初始化 editConfig
  useEffect(() => {
    if (episode && videoClips.length > 0 && !editConfigLoading && !editConfig) {
      // 只有当 editConfig 为 null 时才初始化
      initializeEditConfig();
    }
  }, [episode, videoClips, editConfigLoading, editConfig, initializeEditConfig]);

  // 检查页面是否准备就绪
  useEffect(() => {
    const isReady = !episodesLoading &&
      !videoClipsLoading &&
      !editConfigLoading &&
      episode &&
      videoClips.length >= 0 &&
      editConfig;

    if (isReady && !isPageReady) {
      // 添加小延迟确保所有组件都准备好
      setTimeout(() => setIsPageReady(true), 100);
    }
  }, [episodesLoading, videoClipsLoading, editConfigLoading, episode, videoClips, editConfig, isPageReady]);

  // 当 videoClips 更新时，同步更新 selectedVideoClip
  useEffect(() => {
    if (selectedVideoClip) {
      const updatedClip = videoClips.find(clip => clip.id === selectedVideoClip.id);
      if (updatedClip) {
        setSelectedVideoClip(updatedClip);
      }
    }
  }, [videoClips]);

  // 首次进入时自动选中第一个clip
  useEffect(() => {
    if (!selectedVideoClip && videoClips.length > 0) {
      setSelectedVideoClip(videoClips[0]);
    }
  }, [videoClips, selectedVideoClip]);

  // 点击外部关闭导出下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
      if (batchVideoDropdownRef.current && !batchVideoDropdownRef.current.contains(event.target as Node)) {
        setShowBatchVideoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  const handleBack = () => {
    pauseActionRef.current?.();
    // 添加小延迟确保音频停止后再导航
    setTimeout(() => {
      navigate(`/project/${episode?.projectId}?tab=episodes`);
    }, 100);
  };

  const handleBatchVideoGeneration = async (params: any) => {
    try {
      await batchGenerateVideos(currentEpisodeId, params);
      setShowBatchVideoModal(false);
      setShowBatchVideoDropdown(false);
      success(t('editor:batchVideo.success'));
    } catch (err) {
      console.error('Batch video generation failed:', err);
      error(t('editor:batchVideo.failed'));
    }
  };

  const handleBatchGeneratePrompts = async () => {
    setIsBatchGeneratingPrompts(true);
    setShowBatchVideoDropdown(false);
    try {
      const result = await episodeApi.batchGenerateVideoPrompts(currentEpisodeId);

      // 轮询任务状态
      const pollStatus = async () => {
        try {
          const status = await episodeApi.getBatchPromptGenerationStatus(currentEpisodeId, result.jobId);

          if (status.status === 'completed') {
            // 刷新所有clips数据
            await refetchVideoClips();
            success(t('editor:batchVideo.promptSuccess', { success: status.successCount, total: status.total }));
            setIsBatchGeneratingPrompts(false);
          } else if (status.status === 'failed') {
            error(`${t('editor:batchVideo.promptFailed')}: ${status.error || 'Unknown error'}`);
            setIsBatchGeneratingPrompts(false);
          } else {
            // 继续轮询
            setTimeout(pollStatus, 2000);
          }
        } catch (err) {
          console.error('Failed to query task status:', err);
          setIsBatchGeneratingPrompts(false);
          error(t('editor:queryStatusFailed'));
        }
      };

      // 开始轮询
      setTimeout(pollStatus, 2000);
    } catch (err) {
      console.error('Failed to batch generate prompts:', err);
      error(t('editor:batchVideo.promptFailed'));
      setIsBatchGeneratingPrompts(false);
    }
  };

  const handleExportVideo = async () => {
    if (!editConfig) {
      error(t('editor:editConfigNotLoaded'));
      return;
    }

    try {
      const config = {
        format: 'mp4' as const,
        quality: 'high' as const,
        resolution: '1280x720',
        fps: 30
      };

      // 提交导出任务
      const jobResponse = await exportVideo(currentEpisodeId, config);

      // 轮询任务状态
      const finalStatus = await pollExportStatus(currentEpisodeId, jobResponse.jobId);

      if (finalStatus.videoUrl) {
        // 设置成功状态，显示下载按钮
        setExportSuccess({
          videoUrl: finalStatus.videoUrl,
          filename: `${episode?.title || 'video'}.mp4`
        });
      } else {
        error(t('editor:exportFailedNoUrl'));
      }
    } catch (err) {
      console.error('Export failed:', err);
      error(t('editor:export.failed') + ': ' + (err instanceof Error ? err.message : 'Unknown error'));
    }

    setShowExportDropdown(false);
  };

  const handleDownloadVideo = async () => {
    if (!exportSuccess) return;

    try {
      setDownloadProgress(0);

      const response = await fetch(exportSuccess.videoUrl);
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Cannot read response stream');

      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        received += value.length;

        if (total > 0) {
          setDownloadProgress(Math.round((received / total) * 100));
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = exportSuccess.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
      setDownloadProgress(null);
      success(t('editor:downloadComplete'));
    } catch (error) {
      setDownloadProgress(null);
      console.error('Download failed:', error);
      // 降级到直接打开链接
      window.open(exportSuccess.videoUrl, '_blank');
      success(t('editor:videoOpenedInNewWindow'));
    }
  };



  const handleExportStoryboard = async () => {
    if (!editConfig) {
      error(t('editor:editConfigNotLoaded'));
      return;
    }

    setShowPreExportLoading(true);
    setShowExportDropdown(false);

    // 获取背景音乐URL（如果有的话）
    const bgmUrl = editConfig.tracks?.bgm?.enabled ? episode?.bgmUrl : undefined;

    await startStoryboardExport(videoClips, editConfig, episode, bgmUrl);
    setShowPreExportLoading(false);
  };

  const handleTitleSave = async (title: string) => {
    if (!episode) return;

    setIsTitleSaving(true);
    try {
      await episodeApi.updateEpisode(episode.id, { title });
      await refreshEpisode();
    } catch (error) {
      console.error('修改剧集标题失败:', error);
    } finally {
      setIsTitleSaving(false);
    }
  };

  // 检查发言者是否为旁白
  const isNarrator = (speaker?: string): boolean => {
    if (!speaker) return false;
    const normalizedSpeaker = speaker.trim().toLowerCase();
    const narratorKeywords = ['narrator', '旁白', '叙述者', 'narrative', 'narration'];
    return narratorKeywords.some(keyword => normalizedSpeaker.includes(keyword));
  };

  const handleClipInsertion = async (newClip: any, afterClipId: string) => {
    // 无感刷新 videoClips 数据
    await refetchVideoClips();

    // 直接在 editConfig 中插入新 clip 数据
    if (editConfig && newClip) {
      const afterClipIndex = editConfig.clips.findIndex(c => c.clipId === afterClipId);
      if (afterClipIndex !== -1) {
        const afterClipInfo = editConfig.clips[afterClipIndex];
        const insertPosition = afterClipInfo.startTime + afterClipInfo.duration;

        // 根据发言者类型设置音量和对话音量
        // 旁白：视频轨道静音，对话轨道不静音
        // 非旁白：视频轨道不静音，对话轨道静音
        const speaker = newClip.speaker;
        const newClipInfo = {
          clipId: newClip.id,
          startTime: insertPosition,
          duration: 5, // 默认5秒
          videoVolume: isNarrator(speaker) ? 0 : 1,
          dialogueVolume: isNarrator(speaker) ? 1 : 0,
          showSubtitle: true,
          transition: { type: 'cut' as const, duration: 0 }
        };

        // 更新后续 clips 的 startTime
        const updatedClips = [...editConfig.clips];
        for (let i = afterClipIndex + 1; i < updatedClips.length; i++) {
          updatedClips[i].startTime += 5; // 增加新 clip 的时长
        }

        // 插入新 clip
        updatedClips.splice(afterClipIndex + 1, 0, newClipInfo);

        // 更新 editConfig
        const newEditConfig = {
          ...editConfig,
          timeline: {
            ...editConfig.timeline,
            totalDuration: editConfig.timeline.totalDuration + 5
          },
          clips: updatedClips
        };

        updateEditConfig(newEditConfig);
      }
    }
  };
  if (!isPageReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-page">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-lg">{t('editor:loading')}</p>
          <p className="text-gray-500 text-sm mt-2">{t('editor:loadingHint')}</p>
        </div>
      </div>
    );
  }

  // 如果没有集，显示提示
  if (!episodesLoading && !episode) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-page">
        <div className="text-center">
          <p className="text-gray-400 mb-4">{t('editor:notFound')}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg cursor-pointer transition-colors"
          >
            {t('editor:backToEpisodes')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900 overflow-hidden">
      {/* Left Section: Header + Content + Timeline */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 flex items-center justify-between px-6 border-b border-border-subtle flex-shrink-0 bg-bg-surface">
          {/* 左侧：返回按钮和标题 */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-gray-50 hover:bg-bg-elevated rounded-lg transition-colors flex-shrink-0"
              aria-label="返回"
            >
              <ArrowLeftIcon className="w-[18px] h-[18px]" />
            </button>
            <EditableHeaderTitle
              title={episode?.title || ''}
              fallbackTitle="短剧详情"
              onSave={handleTitleSave}
              isSaving={isTitleSaving}
              titleClassName="text-[15px] font-semibold text-gray-50"
              maxWidthClassName="max-w-[260px]"
            />
          </div>

          {/* 中间：Tab 切换 */}
          <div className="flex items-center gap-1 p-1 bg-bg-elevated rounded-lg">
            <button
              onClick={() => {
                if (currentEpisodeId) {
                  navigate(`/episode/${currentEpisodeId}/script-generation`);
                }
              }}
              className="px-5 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
            >
              {t('editor:scriptPlanning')}
            </button>
            <button
              className="px-5 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary transition-colors"
            >
              {t('editor:imageEditing')}
            </button>
          </div>

          {/* 右侧：操作按钮 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Batch Video Dropdown */}
            <div className="relative" ref={batchVideoDropdownRef}>
              <button
                onClick={() => setShowBatchVideoDropdown(!showBatchVideoDropdown)}
                disabled={isBatchGeneratingPrompts}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600/50 hover:bg-green-600/60 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="一键图转视频"
              >
                <VideoCameraIcon className="w-4 h-4 mr-2" />
                {isBatchGeneratingPrompts ? t('editor:batchVideo.generating') : t('editor:batchVideo.start')}
                <ChevronDownIcon className="w-4 h-4 ml-1" />
              </button>

              {/* Batch Video Options Dropdown */}
              {showBatchVideoDropdown && (
                <div className="absolute top-full right-0 mt-2 bg-bg-elevated border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[180px]">
                  <button
                    onClick={handleBatchGeneratePrompts}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-bg-surface transition-colors text-gray-300 flex items-center cursor-pointer"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    {t('editor:generateVideoPrompts')}
                  </button>
                  <button
                    onClick={() => {
                      setShowBatchVideoModal(true);
                      setShowBatchVideoDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-bg-surface transition-colors text-gray-300 flex items-center cursor-pointer"
                  >
                    <VideoCameraIcon className="w-4 h-4 mr-2" />
                    {t('editor:generateAllVideoClips')}
                  </button>
                </div>
              )}
            </div>

            {/* Export Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={!videoClips.length || isLocalExporting || isStoryboardExporting || !!cloudExportProgress}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary/50 hover:bg-primary/60 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                {isLocalExporting || isStoryboardExporting || cloudExportProgress ? t('editor:export.exporting') : t('editor:export.title')}
                <ChevronDownIcon className="w-4 h-4 ml-1" />
              </button>

              {/* Export Options Dropdown */}
              {showExportDropdown && (
                <div className="absolute top-full right-0 mt-2 bg-bg-elevated border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[160px]">
                  <button
                    onClick={handleExportVideo}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-bg-surface transition-colors text-gray-300 flex items-center cursor-pointer"
                  >
                    <FilmIcon className="w-4 h-4 mr-2" />
                    {t('editor:exportFullVideo')}
                  </button>
                  <button
                    onClick={handleExportStoryboard}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-bg-surface transition-colors text-gray-300 flex items-center cursor-pointer"
                  >
                    <PhotoIcon className="w-4 h-4 mr-2" />
                    {t('editor:exportStoryboardAssets')}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowAssetsDrawer(true)}
              className="inline-flex items-center p-2 text-sm font-medium text-white bg-purple-600/50 hover:bg-purple-600/60 rounded-lg transition-colors"
              title="主体设定"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <VideoEditorProvider
            videoClips={videoClips}
            episode={episode}
            editConfig={editConfig}
            onEditConfigChange={updateEditConfig}
            onSaveEditConfigImmediately={saveEditConfig}
            initialSelectedClip={selectedVideoClip}
            resourceStatusMap={resourceStatusMap}
            pendingClipIds={pendingClipIds}
            isResourcePolling={isResourcePolling}
            onTriggerResourcePoll={triggerResourcePoll}
            onResourceStatusUpdate={handleResourceStatusUpdate}
          >
            {/* 播放清理组件 */}
            <PlaybackCleanup />

            {/* 同步暂停函数 */}
            <PauseActionSync pauseActionRef={pauseActionRef} />

            {/* 同步 Context 中的 selectedClip 和 selectedClipView 到父组件 */}
            <VideoEditorSync
              onSelectedClipChange={setSelectedVideoClip}
              onSelectedClipViewChange={(view) => {
                if (!view) return;

                // 只在视图真正变化时才自动切换 tab
                if (view === lastViewRef.current) {
                  return; // 视图没有变化，不触发切换
                }

                // 更新上一次的视图记录
                lastViewRef.current = view;

                // 轨道切换时自动切换右侧 ResourcePanel 的 tab
                if (view === 'video') {
                  // 视频视图：如果有视频显示video tab，否则显示image tab
                  setActiveResourceTab(selectedVideoClip?.videoUrl ? 'video' : 'image');
                } else if (view === 'audio') {
                  // 音频视图：显示audio tab
                  setActiveResourceTab('audio');
                } else if (view === 'text') {
                  // 字幕视图：显示storyboard tab（字幕对应画面编辑）
                  setActiveResourceTab('storyboard');
                }
              }}
            />

            {/* Preview Area */}
            <div className="flex-1 flex flex-row overflow-hidden bg-gray-900 relative">
              <PreviewAreaWithTabs
                selectedVideoClip={selectedVideoClip}
                episode={episode}
                refreshVideoClip={refreshVideoClip}
                refreshEpisode={refreshEpisode}
                imageFrameType={imageFrameType}
                setImageFrameType={setImageFrameType}
              />

              {/* 生成进度指示器 - 浮动在预览区左下角 */}
              {videoClips.length > 0 && (
                <div className="absolute bottom-2 left-2 z-10">
                  <GenerationProgress videoClips={videoClips} editConfig={editConfig} />
                </div>
              )}
            </div>

            {/* Bottom Panel - Timeline */}
            <div className="h-62 bg-gray-800 border-t border-gray-700 overflow-hidden">
              <Timeline
                onClipInserted={handleClipInsertion}
                onClipDeleted={async () => {
                  // 刷新 videoClips 数据
                  await refetchVideoClips();
                }}
                onClipCopied={handleClipInsertion}
                refreshEpisode={refreshEpisode}
              />
            </div>
          </VideoEditorProvider>
        </div>
      </div>

      {/* Right Panel - Resources (Full Height) */}
      <div className={`relative flex-shrink-0 transition-all duration-300 ease-in-out ${isResourcePanelCollapsed ? 'w-0' : 'w-96'}`}>
        {/* 折叠/展开按钮 */}
        <button
          onClick={() => setIsResourcePanelCollapsed(!isResourcePanelCollapsed)}
          className={`absolute top-1/2 -translate-y-1/2 z-10 w-5 h-12 flex items-center justify-center bg-bg-elevated hover:bg-border-default transition-colors ${isResourcePanelCollapsed ? 'right-0 rounded-l-md' : '-left-5 rounded-l-md'}`}
          title={isResourcePanelCollapsed ? t('editor:panel.expand') : t('editor:panel.collapse')}
        >
          {isResourcePanelCollapsed ? (
            <ChevronLeftIcon className="w-4 h-4 text-gray-300" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-300" />
          )}
        </button>

        {/* Panel 内容 */}
        <div className={`h-full bg-bg-surface border-l border-border-subtle overflow-hidden transition-opacity duration-300 ${isResourcePanelCollapsed ? 'opacity-0' : 'opacity-100'}`}>
          <ResourcePanel
            selectedVideoClip={selectedVideoClip}
            activeTab={activeResourceTab}
            onTabChange={(tab) => {
              // 手动切换资源tab
              setActiveResourceTab(tab);
            }}
            imageFrameType={imageFrameType}
            onImageFrameTypeChange={setImageFrameType}
            refreshVideoClip={refreshVideoClip}
            editConfig={editConfig}
            onEditConfigChange={updateEditConfig}
            resourceStatusMap={resourceStatusMap}
            pendingClipIds={pendingClipIds}
            onTriggerResourcePoll={triggerResourcePoll}
            episode={episode}
          />
        </div>
      </div>

      {/* 收起状态下的展开按钮（固定在右边缘） */}
      {isResourcePanelCollapsed && (
        <button
          onClick={() => setIsResourcePanelCollapsed(false)}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-20 w-5 h-12 flex items-center justify-center bg-bg-elevated hover:bg-border-default rounded-l-md transition-colors cursor-pointer"
          title="展开资源面板"
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-300" />
        </button>
      )}

      {/* Project Assets Drawer */}
      {currentEpisodeId && (
        <ProjectAssetsDrawer
          isOpen={showAssetsDrawer}
          episodeId={episode?.id}
          projectId={episode?.projectId}
          onClose={() => setShowAssetsDrawer(false)}
        />
      )}

      {/* Batch Video Generation Modal */}
      <BatchVideoGenerationModal
        isOpen={showBatchVideoModal}
        onClose={() => setShowBatchVideoModal(false)}
        onSubmit={handleBatchVideoGeneration}
        loading={batchVideoLoading}
        episode={episode}
        videoClips={videoClips}
      />

      {/* Export Loading Overlay */}
      {(isLocalExporting && localExportProgress) && (
        <ExportLoadingOverlay
          progress={localExportProgress}
          onCancel={cancelExport}
        />
      )}

      {/* Cloud Export Loading Overlay */}
      {cloudExportProgress && (
        <ExportLoadingOverlay
          progress={{
            stage: 'exporting' as any,
            progress: cloudExportProgress.progress,
            message: cloudExportProgress.message
          }}
          onCancel={() => { }} // 云端导出暂不支持取消
        />
      )}

      {/* Pre-Export Loading */}
      <GlobalLoading isOpen={showPreExportLoading} message={t('editor:prepare')} />

      {/* Batch Generate Prompts Loading */}
      <GlobalLoading isOpen={isBatchGeneratingPrompts} message={t('editor:preparingPrompts')} />

      {/* Export Success Overlay */}
      {
        exportSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-bg-surface rounded-lg p-8 max-w-md w-full mx-4 text-center">
              {downloadProgress !== null ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <h3 className="text-xl font-semibold text-gray-50 mb-2">{t('editor:export.downloading')}</h3>
                  <div className="w-full bg-bg-elevated rounded-full h-2 mb-4">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${downloadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-gray-400">{downloadProgress}%</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-success rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-50 mb-2">{t('editor:export.success')}</h3>
                  <p className="text-gray-400 mb-6">{t('editor:export.successHint')}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDownloadVideo}
                      className="flex-1 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors cursor-pointer"
                    >
                      {t('editor:export.download')}
                    </button>
                    <button
                      onClick={() => setExportSuccess(null)}
                      className="flex-1 px-4 py-2 bg-bg-elevated hover:bg-border-default text-gray-200 rounded-lg transition-colors cursor-pointer"
                    >
                      {t('common:close')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      }
    </div >
  );
}
